/**
 * Catalogue MRH (Habitation) MonGustave — dérivé de mrh_lead_mapping.csv.
 * Seuls ces attributs sont affichés sur la fiche CRM.
 */

export const MRH_GROUP_ORDER = [
  'LOGEMENT',
  'ADRESSE DU BIEN',
  'ÉQUIPEMENTS',
  'ASSURANCE / SINISTRES',
  'IDENTITÉ',
  'CONTRAT'
]

const OUI_NON = { OUI: 'Oui', NON: 'Non' }

const SINISTRE_TYPES = {
  DEGAT_DES_EAUX: 'Dégât des eaux',
  RESPONSABILITE_CIVILE: 'Responsabilité civile',
  VOL: 'Vol',
  BRIS_DE_GLACE: 'Bris de glace',
  DOMMAGE_ELECTRIQUE: 'Dommage électrique',
  VANDALISME: 'Vandalisme',
  INCENDIE: 'Incendie',
  EVENEMENTS_CLIMATIQUES: 'Événements climatiques',
  CATASTROPHE_NATURELLE: 'Catastrophe naturelle',
  INONDATION: 'Inondation',
  AUTRE: 'Autre'
}

const has = (v, key) =>
  String(v || '')
    .split(';')
    .map((s) => s.trim().toLowerCase())
    .includes(String(key).toLowerCase())

const childFields = []
for (let n = 1; n <= 9; n++) {
  childFields.push({
    key: 'date_naissance_enfant' + n,
    label: 'Date de naissance enfant ' + n,
    group: 'IDENTITÉ',
    when: (v) => Number(v.nbr_enfants || 0) >= n
  })
}

const sinistreFields = []
for (let n = 1; n <= 3; n++) {
  const ord = n === 1 ? '1er' : n + 'e'
  const when = (v) => Number(v.nbr_sinistres || 0) >= n
  sinistreFields.push(
    { key: 'type_sinistre' + n, label: ord + ' sinistre - Type', group: 'ASSURANCE / SINISTRES', values: SINISTRE_TYPES, when },
    { key: 'mois_SinDate' + n, label: ord + ' sinistre - Mois', group: 'ASSURANCE / SINISTRES', when },
    { key: 'annee_SinDate' + n, label: ord + ' sinistre - Année', group: 'ASSURANCE / SINISTRES', when }
  )
}

export const MRH_FIELDS = [
  {
    key: 'type_habitation',
    label: 'Type de logement',
    group: 'LOGEMENT',
    values: { APPARTEMENT: 'Un appartement', MAISON: 'Une maison' }
  },
  {
    key: 'etage_appartement',
    label: 'Étage',
    group: 'LOGEMENT',
    values: { REZ_DE_CHAUSSEE: 'RDC', AUTRE_ETG: 'Étage intermédiaire', DERNIER_ETG: 'Dernier étage' },
    when: (v) => String(v.type_habitation || '').toUpperCase() === 'APPARTEMENT'
  },
  {
    key: 'statut_resident',
    label: 'Vous en êtes',
    group: 'LOGEMENT',
    values: {
      LOCATAIRE_COLOCATAIRE: 'Locataire',
      LOCATAIRE_COLOCATAIRE_MEUBLE: 'Locataire en meublé',
      PROPRIETAIRE_OCCUPANT: 'Propriétaire occupant',
      PROPRIETAIRE_NON_OCCUPANT: 'Propriétaire non occupant',
      OCCUPANT_TITRE_GRATUIT: 'Occupant à titre gratuit'
    }
  },
  {
    key: 'deja_proprietaire_logement',
    label: 'Déjà propriétaire de ce logement',
    group: 'LOGEMENT',
    values: OUI_NON,
    when: (v) => /^PROPRIETAIRE_/.test(String(v.statut_resident || '').toUpperCase())
  },
  {
    key: 'type_residence',
    label: 'Type de résidence',
    group: 'LOGEMENT',
    values: { RESIDENCE_PRINCIPALE: 'Résidence principale', RESIDENCE_SECONDAIRE: 'Résidence secondaire' }
  },
  {
    key: 'anciennete_logement',
    label: 'Construction du logement',
    group: 'LOGEMENT',
    values: {
      MOINS_DE_5_ANS: 'Il y a moins de 5 ans',
      ENTRE_5_ET_10_ANS: 'Entre 5 ans et 10 ans',
      ENTRE_10_ET_30_ANS: 'Entre 10 ans et 30 ans',
      PLUS_DE_30_ANS: 'Il y a plus de 30 ans',
      EN_CONSTRUCTION: 'En construction/rénovation'
    }
  },
  { key: 'logement_cond', label: 'Habite ce logement actuellement', group: 'LOGEMENT', values: OUI_NON },
  {
    key: 'annee_demmenagement',
    label: "Année d'emménagement",
    group: 'LOGEMENT',
    when: (v) =>
      String(v.anciennete_logement || '').toUpperCase() !== 'EN_CONSTRUCTION' &&
      String(v.logement_cond || '').toUpperCase() === 'OUI'
  },
  {
    key: 'emmenagement_prevus',
    label: "Emménagement prévu",
    group: 'LOGEMENT',
    when: (v) => String(v.anciennete_logement || '').toUpperCase() === 'EN_CONSTRUCTION'
  },
  {
    key: 'logement_proposer_a_location',
    label: 'Logement proposé à la location',
    group: 'LOGEMENT',
    values: OUI_NON,
    when: (v) => String(v.type_residence || '').toUpperCase() !== 'RESIDENCE_PRINCIPALE'
  },
  {
    key: 'fins_professionnelles',
    label: 'Usage professionnel',
    group: 'LOGEMENT',
    values: OUI_NON,
    when: (v) => String(v.statut_resident || '').toUpperCase() !== 'PROPRIETAIRE_NON_OCCUPANT'
  },
  {
    key: 'nbr_jours_inhabita',
    label: 'Jours inhabités par an',
    group: 'LOGEMENT',
    values: {
      MOINS_45_JRS: 'Entre 0 et 45 jours',
      ENTRE_45_ET_60_JRS: 'Entre 45 et 60 jours',
      ENTRE_60_ET_90_JRS: 'Entre 60 et 90 jours',
      PLUS_DE_90_JRS: 'Entre 90 et 180 jours',
      PLUS_DE_180_JRS: 'Plus de 180 jours'
    }
  },
  { key: 'surface_habitable', label: 'Surface habitable (m²)', group: 'LOGEMENT' },
  { key: 'inferieures_a_30', label: 'Pièces < 30 m²', group: 'LOGEMENT' },
  { key: 'entre_30_40', label: 'Pièces 30-40 m²', group: 'LOGEMENT' },
  { key: 'entre_40_50', label: 'Pièces 40-50 m²', group: 'LOGEMENT' },
  { key: 'superieures_50', label: 'Pièces > 50 m²', group: 'LOGEMENT' },

  { key: 'cp_logment', label: 'Code postal du logement', group: 'ADRESSE DU BIEN' },
  { key: 'ville_logment', label: 'Ville du logement', group: 'ADRESSE DU BIEN' },

  { key: 'capital_mobilier', label: 'Capital mobilier (€)', group: 'ÉQUIPEMENTS' },
  {
    key: 'equipent_logement',
    label: 'Le logement comporte',
    group: 'ÉQUIPEMENTS',
    multi: true,
    values: {
      veranda: 'Véranda ou loggia',
      annexes: 'Annexes ou dépendances',
      garage: 'Garage',
      chauffage: 'Moyens de chauffage (bois)',
      antivol: "Système d'alarme antivol",
      piscine: 'Piscine enterrée'
    }
  },
  {
    key: 'surface_annexes',
    label: 'Surface annexes (m²)',
    group: 'ÉQUIPEMENTS',
    when: (v) => has(v.equipent_logement, 'Annexes')
  },
  {
    key: 'surface_veranda',
    label: 'Surface véranda (m²)',
    group: 'ÉQUIPEMENTS',
    when: (v) => has(v.equipent_logement, 'Veranda')
  },
  {
    key: 'type_chauffage',
    label: 'Type de chauffage',
    group: 'ÉQUIPEMENTS',
    values: {
      poele_a_bois: 'Poêle à bois',
      cheminee_insert: 'Cheminée à foyer ouvert',
      cheminee_sans_insert: 'Cheminée à foyer fermé/insert'
    },
    when: (v) => has(v.equipent_logement, 'chauffage')
  },
  {
    key: 'cheminee_professionel',
    label: 'Cheminée posée par un professionnel',
    group: 'ÉQUIPEMENTS',
    values: { OUI: 'Oui', NON: 'Non', 'je-ne-sais-pas': 'Je ne sais pas' },
    when: (v) => /^cheminee_/.test(String(v.type_chauffage || ''))
  },
  {
    key: 'type_piscine',
    label: 'Type de piscine',
    group: 'ÉQUIPEMENTS',
    values: {
      piscine_interieure: 'Piscine intérieure',
      piscine_exterieure_couverte: 'Piscine extérieure couverte',
      piscine_exterieure_non_couverte: 'Piscine extérieure non couverte'
    },
    when: (v) => has(v.equipent_logement, 'piscine')
  },
  {
    key: 'moyen_de_protection_detail',
    label: 'Systèmes de protection',
    group: 'ÉQUIPEMENTS',
    multi: true,
    values: {
      alarme: 'Alarme',
      'detecteur-de-fumee': 'Détecteur de fumée',
      porte_blindee: 'Porte blindée',
      serrure_3_points: 'Serrure 3 points',
      videosurveillance: 'Vidéosurveillance',
      telesurveillance: 'Télésurveillance'
    },
    when: (v) => has(v.equipent_logement, 'antivol')
  },

  {
    key: 'etat_assureur',
    label: 'Actuellement assuré',
    group: 'ASSURANCE / SINISTRES',
    values: { OUI: 'Oui', NON: 'Non', 'jamais-assure': "Je n'ai jamais été assuré" }
  },
  {
    key: 'assureur_habitation',
    label: 'Assureur actuel / précédent',
    group: 'ASSURANCE / SINISTRES',
    values: { autre: 'Aucun de ces assureurs' },
    when: (v) => String(v.etat_assureur || '').toLowerCase() !== 'jamais-assure'
  },
  {
    key: 'duree_innassure',
    label: 'Non assuré depuis',
    group: 'ASSURANCE / SINISTRES',
    values: { MOINS_1_MOIS: "Moins d'un mois", PLUS_1_MOIS: "Plus d'un mois" },
    when: (v) => String(v.etat_assureur || '').toUpperCase() === 'NON'
  },
  { key: 'nbr_sinistres', label: 'Sinistres (3 dernières années)', group: 'ASSURANCE / SINISTRES' },
  ...sinistreFields,
  {
    key: 'res_3_dernieres_ann',
    label: 'Résiliation (36 derniers mois)',
    group: 'ASSURANCE / SINISTRES',
    values: OUI_NON,
    when: (v) => String(v.etat_assureur || '').toLowerCase() !== 'jamais-assure'
  },

  { key: 'civilite', label: 'Civilité', group: 'IDENTITÉ' },
  { key: 'nom', label: 'Nom', group: 'IDENTITÉ' },
  { key: 'prenom', label: 'Prénom', group: 'IDENTITÉ' },
  { key: 'date_naissance', label: 'Date de naissance', group: 'IDENTITÉ' },
  {
    key: 'situation_matrimoniale',
    label: 'Situation familiale',
    group: 'IDENTITÉ',
    values: {
      celibataire: 'Célibataire',
      marie: 'Marié(e)',
      divorce: 'Divorcé(e)',
      pacse: 'Pacsé(e)',
      'en-concubinage': 'En concubinage',
      separe: 'Séparé(e)',
      veuf: 'Veuf(ve)'
    }
  },
  {
    key: 'profession',
    label: 'Profession',
    group: 'IDENTITÉ',
    values: {
      salarie: 'Salarié',
      retraite: 'Retraité',
      etudiant: 'Étudiant',
      salarie_cadre: 'Salarié cadre',
      fonction_publique_d_etat: "Fonction publique d'état",
      fonction_publique_territoriale: 'Fonction publique territoriale',
      'fonction_publique_hospitalière': 'Fonction publique hospitalière',
      fonction_publique_hospitaliere: 'Fonction publique hospitalière',
      artisan: 'Artisan',
      commercant: 'Commerçant',
      profession_liberale: 'Profession libérale',
      chef_d_entreprise: "Chef d'entreprise",
      enseignant: 'Enseignant',
      agriculteur: 'Agriculteur',
      exploitant_agricole: 'Exploitant agricole',
      vrp: 'VRP',
      visiteur_medical: 'Visiteur médical',
      en_recherche_d_emploi: "En recherche d'emploi",
      sans_profession: 'Sans profession'
    }
  },
  { key: 'nbr_adultes', label: 'Adultes au foyer', group: 'IDENTITÉ' },
  { key: 'nbr_enfants', label: 'Enfants (moins de 18 ans)', group: 'IDENTITÉ' },
  ...childFields,
  { key: 'telephone', label: 'Téléphone', group: 'IDENTITÉ' },
  { key: 'email', label: 'Email', group: 'IDENTITÉ' },
  { key: 'adresse', label: 'Adresse', group: 'IDENTITÉ' },
  { key: 'cp', label: 'Code postal', group: 'IDENTITÉ' },
  { key: 'ville', label: 'Ville', group: 'IDENTITÉ' },

  { key: 'date_effet', label: "Date d'effet", group: 'CONTRAT' },
  {
    key: 'accepte_offre',
    label: 'Offres partenaires',
    group: 'CONTRAT',
    values: { 1: 'Accepté', 0: 'Refusé' }
  }
]

const FIELD_BY_KEY = MRH_FIELDS.reduce((acc, f) => {
  acc[f.key.toLowerCase()] = f
  return acc
}, {})

export function mrhFieldMeta(mapping) {
  return FIELD_BY_KEY[String(mapping || '').trim().toLowerCase()] || null
}

function translateOne(field, raw) {
  const s = String(raw).trim()
  if (!field.values) return s
  return field.values[s] || field.values[s.toUpperCase()] || field.values[s.toLowerCase()] || s
}

/** Valeur stockée MG → libellé affiché (gère les listes "a;b"). */
export function mrhDisplayValue(field, raw) {
  if (!field) return raw
  if (field.multi) {
    return String(raw)
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => translateOne(field, s))
      .join(', ')
  }
  return translateOne(field, raw)
}

export function isMrhFieldVisible(field, valuesByKey) {
  return !field.when || field.when(valuesByKey)
}
